import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import {
  AUTH_REVIEW_REQUIRED,
  NO_AUTH_CHANGE_REQUIRED,
  authorizationApprovalLabel,
  authorizationDeltaKey,
  buildAuthorizationSelectionPayload,
  buildFinalAuthorizationDelta,
  groupAuthorizationRows,
  readPostChangeReview,
  seedAuthorizationSelection,
} from '@/lib/menuChangeReview';

const GROUPS = [
  ['introduced', 'Newly introduced authorizations', 'Checked means retain.'],
  ['additions', 'HALO-recommended additions', 'Selected only where the trace evidence is strong. You can clear a row.'],
  ['removals', 'Possible removals', 'Limited to permissions this change introduced. Unchecked until you select one. A value missing from one trace is not a removal.'],
];

function deltaText(line) {
  const sign = line.op === 'add' ? '+' : line.op === 'remove' ? '−' : 'exclude';
  const where = [line.object, line.instance_id || 'no instance', line.field, line.value].join(' / ');
  return `${sign} ${where}${line.why ? ` — Why? ${line.why}` : ''}`;
}

function ValueGroups({ group, rows, selection, onToggle, testId, canMutate }) {
  const grouped = groupAuthorizationRows(rows);
  if (!grouped.length) return <p className="text-muted-foreground">None</p>;
  return (
    <div className="space-y-3" data-testid={testId}>
      {grouped.map((objectGroup) => (
        <div key={objectGroup.object}>
          <p className="font-mono text-sm font-semibold">{objectGroup.object}</p>
          {objectGroup.instances.map((instance) => (
            <div key={`${objectGroup.object}-${instance.instance_id || 'none'}`} className="ml-3 mt-1">
              <p className="text-xs text-muted-foreground">
                Instance {instance.instance_id || 'not provided'}
              </p>
              {instance.fields.map((field) => (
                <div key={field.field} className="ml-3">
                  <p className="text-xs font-medium">{field.field}</p>
                  <ul className="space-y-1">
                    {field.values.map((row) => {
                      const checked = Boolean(selection[group]?.[row.id]);
                      return (
                        <li key={row.id}>
                          <label className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="mt-1"
                              data-testid={`auth-delta-${group}-${row.id}`}
                              checked={checked}
                              disabled={!row.interactive || !canMutate}
                              title={permissionDeniedTitle(canMutate, 'change the authorization selection')}
                              onChange={() => onToggle(group, row.id, !checked)}
                            />
                            <span>
                              <span className="font-mono">{row.value}</span>
                              {row.activity ? ` · activity ${row.activity}` : ''}
                              {row.org_level ? ` · org ${row.org_level}` : ''}
                              {row.simulated ? ' · SIMULATED' : ''}
                              {row.decisionHint ? ` · ${row.decisionHint}` : ''}
                              {row.why ? <span className="block text-xs text-muted-foreground">Why? {row.why}</span> : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const EMPTY_MANUAL = { op: 'add', object: '', instance_id: '', field: '', value: '', why: '' };

export default function MenuChangeFollowUp({
  ticket,
  onSubmit,
  submitting = false,
  canMutate = false,
  canApprove = canMutate,
}) {
  const review = useMemo(() => readPostChangeReview(ticket), [ticket]);
  const [note, setNote] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [selection, setSelection] = useState(() => seedAuthorizationSelection(review));
  const [manual, setManual] = useState(review.manual);
  const [draft, setDraft] = useState(EMPTY_MANUAL);

  useEffect(() => {
    setSelection(seedAuthorizationSelection(review));
    setManual(review.manual);
  }, [review]);

  const finalDelta = useMemo(
    () => buildFinalAuthorizationDelta({
      introduced: review.introduced,
      additions: review.additions,
      removals: review.removals,
      manual,
      selection,
    }),
    [review, manual, selection],
  );
  const selectionPayload = useMemo(
    () => buildAuthorizationSelectionPayload(review, selection, manual, finalDelta),
    [review, selection, manual, finalDelta],
  );
  const persistedPayload = useMemo(() => {
    const persistedSelection = seedAuthorizationSelection(review);
    const persistedDelta = review.storedDelta.length
      ? review.storedDelta
      : buildFinalAuthorizationDelta({
        introduced: review.introduced,
        additions: review.additions,
        removals: review.removals,
        manual: review.manual,
        selection: persistedSelection,
      });
    return buildAuthorizationSelectionPayload(
      review,
      persistedSelection,
      review.manual,
      persistedDelta,
    );
  }, [review]);
  const deltaKey = authorizationDeltaKey(finalDelta);
  const boundKey = review.approval?.delta_key || '';
  const approvalStatus = review.approval?.status || (review.phase === 'auth_review' ? 'not_requested' : '');
  const selectionChanged = authorizationDeltaKey(selectionPayload) !== authorizationDeltaKey(persistedPayload);
  const staleApproval = Boolean(
    review.approvalStale
    || (lowerApproved(approvalStatus) && selectionChanged)
    || (lowerApproved(approvalStatus) && boundKey && boundKey !== deltaKey),
  );
  const approvalPending = ['pending', 'pending_approval', 'awaiting_approval', 'submitted'].includes(
    String(approvalStatus || '').toLowerCase(),
  );
  const canExecuteSimulation =
    lowerApproved(approvalStatus)
    && !staleApproval
    && review.simulationAvailable;
  const executionStatus = String(review.execution?.status || '');
  const simulationReceipt =
    review.execution?.simulation_receipt || review.execution?.receipt;
  const realWriteBlocker =
    review.execution?.real_write_blocker || review.execution?.blocker;
  const realWriteBlocked = Boolean(
    review.execution?.real_write_blocked === true
    || review.execution?.real_write_status === 'blocked'
    || review.execution?.real_write?.status === 'blocked'
    || executionStatus.toUpperCase().includes('REAL_WRITE_BLOCKED')
    || realWriteBlocker,
  );

  if (review.phase === 'hidden') return null;

  const toggle = (group, id, checked) => {
    setSelection((current) => ({
      ...current,
      [group]: { ...current[group], [id]: checked },
    }));
  };

  const addManual = () => {
    const row = {
      op: draft.op,
      object: draft.object.trim(),
      instance_id: draft.instance_id.trim(),
      field: draft.field.trim(),
      value: draft.value.trim(),
      why: draft.why.trim(),
    };
    if (!row.object || !row.field || !row.value || !row.why) return;
    setManual((current) => [...current, row]);
    setDraft(EMPTY_MANUAL);
  };

  const submitValidation = (outcome) => {
    onSubmit?.({
      action: 'record_task_validation',
      outcome,
      note: note.trim(),
      evidenceFile,
    });
  };

  return (
    <Card data-testid="menu-change-follow-up">
      <CardHeader>
        <CardTitle>Task validation</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        <p>
          The development menu change is recorded. Confirm the intended business task before any
          authorization edit. A trace is optional. Profile generation or user assignment may still
          be required before the task can be exercised.
        </p>
        {review.waivers.length > 0 && (
          <ul data-testid="menu-gate-waivers" className="list-disc pl-5">
            {review.waivers.map((waiver) => (
              <li key={waiver.key}>
                {waiver.key} waived by {waiver.actor}: {waiver.rationale}
              </li>
            ))}
          </ul>
        )}
        {review.task?.note && <p data-testid="menu-task-recorded-note">{review.task.note}</p>}

        {review.phase === 'no_auth_change' ? (
          <p data-testid="no-auth-change-required" className="rounded-md border border-cp-line px-3 py-2">
            NO_AUTH_CHANGE_REQUIRED. Validation found no authorization gap. The authorization editor is skipped.
          </p>
        ) : null}

        {review.phase === 'task_validation' && (
          <div className="grid gap-2" data-testid="menu-task-validation">
            <Textarea
              data-testid="menu-task-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What was checked, and what was the result?"
            />
            <Input
              type="file"
              data-testid="menu-task-evidence"
              onChange={(event) => setEvidenceFile(event.target.files?.[0] || null)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                data-testid="menu-task-no-auth"
                disabled={!canMutate || submitting}
                title={permissionDeniedTitle(canMutate, 'record task validation')}
                onClick={() => submitValidation(NO_AUTH_CHANGE_REQUIRED)}
              >
                No authorization change
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="menu-task-needs-review"
                disabled={!canMutate || submitting}
                title={permissionDeniedTitle(canMutate, 'request authorization review')}
                onClick={() => submitValidation(AUTH_REVIEW_REQUIRED)}
              >
                Authorization review required
              </Button>
            </div>
          </div>
        )}

        {review.phase === 'auth_review' && (
          <div data-testid="auth-delta-editor" className="grid gap-4">
            {review.simulated && (
              <p data-testid="auth-delta-simulated" className="rounded-md border border-cp-mod px-3 py-2 font-semibold text-cp-mod">
                SIMULATED — these authorization rows are not a live merged SAP result.
              </p>
            )}
            <p>
              Compare exact instances, fields, and values. Retaining a yet-unmerged proposal adds it.
              Clearing a value SAP already added removes that new value. This approval is separate from the menu approval.
            </p>
            {GROUPS.map(([group, title, help]) => (
              <section key={group} className="space-y-2 border-t border-cp-line pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
                <p className="text-xs text-muted-foreground">{help}</p>
                <ValueGroups
                  group={group}
                  rows={review[group]}
                  selection={selection}
                  onToggle={toggle}
                  testId={`auth-delta-${group}`}
                  canMutate={canMutate}
                />
              </section>
            ))}

            <section className="space-y-2 border-t border-cp-line pt-3" data-testid="auth-delta-manual">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Manual change</p>
              <div className="grid gap-2 md:grid-cols-3">
                <select
                  data-testid="auth-delta-manual-op"
                  className="h-9 rounded-md border border-input bg-transparent px-2"
                  value={draft.op}
                  onChange={(event) => setDraft((current) => ({ ...current, op: event.target.value }))}
                >
                  <option value="add">+ Add</option>
                  <option value="remove">− Remove</option>
                </select>
                <Input data-testid="auth-delta-manual-object" placeholder="Object" value={draft.object} onChange={(event) => setDraft((current) => ({ ...current, object: event.target.value }))} />
                <Input data-testid="auth-delta-manual-instance" placeholder="Instance" value={draft.instance_id} onChange={(event) => setDraft((current) => ({ ...current, instance_id: event.target.value }))} />
                <Input data-testid="auth-delta-manual-field" placeholder="Field" value={draft.field} onChange={(event) => setDraft((current) => ({ ...current, field: event.target.value }))} />
                <Input data-testid="auth-delta-manual-value" placeholder="Value" value={draft.value} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} />
                <Input data-testid="auth-delta-manual-why" placeholder="Why?" value={draft.why} onChange={(event) => setDraft((current) => ({ ...current, why: event.target.value }))} />
              </div>
              <Button
                type="button"
                variant="outline"
                data-testid="auth-delta-manual-add"
                disabled={!canMutate || submitting}
                title={permissionDeniedTitle(canMutate, 'add a manual authorization entry')}
                onClick={addManual}
              >
                Add manual change
              </Button>
              {manual.length > 0 && (
                <ul className="list-disc pl-5">
                  {manual.map((row) => (
                    <li key={`${row.op}-${row.object}-${row.instance_id}-${row.field}-${row.value}`}>
                      {row.op === 'remove' ? '−' : '+'} {row.object} / {row.instance_id || 'no instance'} / {row.field} / {row.value}
                      {' '}— Why? {row.why}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section data-testid="auth-delta-final" className="space-y-1 border-t border-cp-line pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Exact authorization delta</p>
              {finalDelta.length === 0 ? (
                <p>No authorization values are selected.</p>
              ) : (
                <ul className="list-disc pl-5 font-mono text-xs">
                  {finalDelta.map((line) => (
                    <li key={deltaText(line)}>{deltaText(line)}</li>
                  ))}
                </ul>
              )}
            </section>

            <p data-testid="auth-delta-approval-status">
              Authorization approval: {authorizationApprovalLabel(approvalStatus)}. Separate from the menu approval.
            </p>
            {review.bindingHash && (
              <p data-testid="auth-delta-binding-hash" className="break-all font-mono text-xs">
                Approval binding: {review.bindingHash}
              </p>
            )}
            {staleApproval && (
              <p data-testid="auth-delta-approval-stale">
                Approval invalidated or stale. The current exact delta must be submitted and approved again.
              </p>
            )}
            {review.execution?.simulated === true && (
              <p data-testid="auth-delta-simulation-receipt" className="rounded-md border border-cp-line px-3 py-2">
                SIMULATED receipt — sap_changed={String(review.execution.sap_changed === true)}.
                {simulationReceipt
                  ? ` Receipt ${typeof simulationReceipt === 'string'
                    ? simulationReceipt
                    : simulationReceipt.id || simulationReceipt.hash || 'recorded'}.`
                  : ''}
              </p>
            )}
            {realWriteBlocked && (
              <p data-testid="auth-delta-real-write-blocked" className="rounded-md border border-cp-mod px-3 py-2 text-cp-mod">
                Real authorization write blocked
                {realWriteBlocker ? `: ${realWriteBlocker}` : ''}.
                The server remains authoritative; no SAP write is implied.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                data-testid="auth-delta-submit"
                disabled={!canMutate || submitting}
                title={permissionDeniedTitle(canMutate, 'submit an authorization delta')}
                onClick={() => onSubmit?.({
                  action: 'submit_authorization_delta',
                  note: note.trim(),
                  authorizationSelection: selectionPayload,
                  bindingHash: review.bindingHash,
                })}
              >
                Submit exact authorization delta for approval
              </Button>
              {approvalPending && canApprove && (
                <>
                  <Button
                    type="button"
                    data-testid="auth-delta-approve"
                    disabled={submitting || staleApproval}
                    onClick={() => onSubmit?.({
                      action: 'approve_authorization_delta',
                      note: note.trim(),
                      authorizationSelection: selectionPayload,
                      bindingHash: review.bindingHash,
                    })}
                  >
                    Approve exact authorization delta
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="auth-delta-reject"
                    disabled={submitting}
                    onClick={() => onSubmit?.({
                      action: 'reject_authorization_delta',
                      note: note.trim(),
                      authorizationSelection: selectionPayload,
                      bindingHash: review.bindingHash,
                    })}
                  >
                    Reject authorization delta
                  </Button>
                </>
              )}
              {canExecuteSimulation && canApprove && (
                <Button
                  type="button"
                  variant="outline"
                  data-testid="auth-delta-execute-simulated"
                  disabled={submitting}
                  onClick={() => onSubmit?.({
                    action: 'execute_simulated_authorization_delta',
                    note: note.trim(),
                    authorizationSelection: selectionPayload,
                    bindingHash: review.bindingHash,
                  })}
                >
                  Execute SIMULATED delta (no SAP change)
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function lowerApproved(status) {
  return String(status || '').toLowerCase() === 'approved';
}
